import { SalesCommissionRatesSchema, STANDARD_SALES_DIRECT_POLICY } from '../types/sales-team.js';
import { salesAddMonths, salesMonth } from './sales-commission.js';
export const STANDARD_COMMISSION_ONLY_PLAN = SalesCommissionRatesSchema.parse({
    direct_hardware_margin: { rate_bps: 2500, accelerator_rate_bps: 3000, annual_margin_threshold_cents: { USD: 15000000 } },
    subscription_share: { rate_bps: 2000, months: 12, scope: 'fleet_orders_only' }, own_session_points: STANDARD_SALES_DIRECT_POLICY,
    partner_session_points: 50, session_points_basis: 'evi_percentage_points', partner_activation: { currency: 'USD', first_order_cents: 50000, fourth_charger_cents: 100000 },
    partner_book_margin: { months: 24, ladder: [{ min_active_partners: 1, rate_bps: 1000 }, { min_active_partners: 10, rate_bps: 1250 }, { min_active_partners: 25, rate_bps: 1500 }] }, continuation_months: 12,
});
export function salesWorkAt(person, at) {
    const latest = [...person.employment_history ?? []].filter(h => +new Date(h.effective_at) <= +at).sort((a, b) => +new Date(b.effective_at) - +new Date(a.effective_at))[0];
    const kind = latest?.kind ?? person.kind, method = latest?.payout_method ?? person.payout_method;
    if (!['contractor', 'employee'].includes(kind) || !['connect', 'payroll'].includes(method) || kind === 'employee' && method !== 'payroll')
        throw new Error('Invalid employment or payout evidence.');
    return { kind, payout_method: method, stripe_connect_account_id: latest ? latest.stripe_connect_account_id ?? null : person.stripe_connect_account_id ?? null, plan_id: latest?.plan_id ?? null };
}
export function salesPartnerLaunchCount(launches, at, months = 12) {
    const cutoff = salesAddMonths(at, -months);
    return launches.filter(d => +new Date(d) >= +cutoff && +new Date(d) <= +at).length;
}
export function salesPaceFlags(input, now) {
    const year = salesPartnerLaunchCount(input.launches, now), six = salesPartnerLaunchCount(input.launches, now, 6);
    return { qualifying_launches_12m: year, active: year >= 8, qualifying_launches_6m: six,
        no_submission_60d: !input.submissions.some(s => +s.at >= +now - 60 * 86400000), below_four_launches_6m: six < 4,
        submission_without_order_30d: input.submissions.some(s => !s.paid && +s.at <= +now - 30 * 86400000), offline_charger: input.offline };
}
export function salesEligibility(lines, activePartners, now = new Date()) {
    const [year, month] = salesMonth(now).split('-').map(Number);
    const months = Array.from({ length: 6 }, (_, i) => salesMonth(salesAddMonths(new Date(Date.UTC(year, month - 1, 15)), -i - 1))).reverse();
    const currencies = [...new Set(['USD', ...lines.map(l => l.currency)])];
    return { active_partners: activePartners, partner_target: 15, usd_monthly_target_cents: 850000,
        buckets: currencies.map(currency => { const monthly = months.map(period => ({ period, amount_cents: lines.filter(l => l.period === period && l.currency === currency).reduce((n, l) => n + l.amount_cents, 0) })); return { currency, months: monthly, average_cents: Math.floor(monthly.reduce((n, l) => n + l.amount_cents, 0) / 6), threshold_cents: currency === 'USD' ? 850000 : null }; }), information_only: true };
}
/**
 * How EVI describes a commission-only earner (Sales Partner or Sales Staff
 * contractor) to Stripe when it creates their Express account. Stripe-hosted
 * onboarding skips prefilled fields and asks the person to confirm them.
 * 7399 is "Miscellaneous Business Services" in Stripe's Connect MCC list (not
 * restricted); Stripe lists no referral or commission agent code. jobTitle
 * answers the job title question Stripe asks Canadian individuals.
 */
export const SALES_CONNECT_DEFAULTS = {
    mcc: '7399',
    url: 'https://evinitiative.com',
    productDescription: 'Earns commissions for referring EV charger installations to EV Initiative Inc.',
    jobTitle: 'Sales Partner',
};
/**
 * Capabilities for an Express account that only receives transfers from EVI: Sales Partners, Sales Staff
 * contractors and location electricity accounts. Transfers alone in the US (the platform's country).
 * Stripe rejects transfers alone for a Canadian account unless it is on the recipient service agreement
 * (API error, test mode, 16 October 2026), and cross-border payouts cannot go to recipient accounts
 * (docs.stripe.com/connect/cross-border-payouts), so Canadian accounts request card_payments with transfers
 * on the full agreement. Stripe asks a Canadian individual the same fields either way.
 */
export function salesConnectCapabilities(country) {
    return country === 'US'
        ? { transfers: { requested: true } }
        : { transfers: { requested: true }, card_payments: { requested: true } };
}
//# sourceMappingURL=sales-rep-program.js.map